import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { GeneralDatePicker, GeneralUpload } from 'src/components/general';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { MEDIA_SOCIAL_PLAYER_LIST } from 'src/constants/queryKeys';
import { networkCreateMediaSocialPlayer, networkUpdateMediaSocialPlayer } from 'src/network/mediaSocialPlayer';
import useFormsModalStore from 'src/store/forms';
import { getDefaultValues } from './defaultValues';

// -----------------------------------------------------------------

const MediaSocialPlayerForm = () => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { modal, closeModal } = useFormsModalStore();

  const mediaSocialPlayerDetails = modal?.data;
  const isEditMode = !!mediaSocialPlayerDetails;

  const [startDate, setStartDate] = useState(isEditMode ? new Date(mediaSocialPlayerDetails?.MediaSocialPlayerDateTimeStart) : new Date());
  const [endDate, setEndDate] = useState(isEditMode ? new Date(mediaSocialPlayerDetails?.MediaSocialPlayerDateTimeEnd) : new Date());

  const defaultValues = getDefaultValues(mediaSocialPlayerDetails);

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    trigger,
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      MediaSocialPlayerAppID: -1,
      MediaSocialPlayerApplicationID: +projectId,
      MediaSocialPlayerOrderIndex: +values?.MediaSocialPlayerOrderIndex,
      MediaSocialPlayerDateTimeStart: new Date(startDate),
      MediaSocialPlayerDateTimeEnd: new Date(startDate),
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateMediaSocialPlayer(mediaSocialPlayerDetails?.MediaSocialPlayerID, PAYLOAD);
        if (res?.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
          }
        } else {
          enqueueSnackbar('خطایی در ویرایش آیتم موردنظر رخ داد!', { variant: 'error' });
        }
      } else {
        const res = await networkCreateMediaSocialPlayer(PAYLOAD);
        if (res?.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
        } else {
          enqueueSnackbar('خطایی در ایجاد آیتم موردنظر رخ داد!', { variant: 'error' });
        }
      }

      await queryClient.invalidateQueries([MEDIA_SOCIAL_PLAYER_LIST]);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('خطای ناشناخته رخ داد! لطفا دوباره تلاش کنید.', { variant: 'error' });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="MediaSocialPlayerTitle" type="text" label="عنوان" />

              <RHFTextField name="MediaSocialPlayerHLS" type="text" label="لینک پخش HLS" />

              <GeneralUpload
                label="تصویر کاور"
                id="media_social_player_image_cover"
                name="MediaSocialPlayerImageCover"
                afterChangeAction={(res) => {
                  setValue('MediaSocialPlayerImageCover', res);
                  trigger('MediaSocialPlayerImageCover');
                }}
              />

              <GeneralUpload
                label="تصویر پس زمنیه"
                id="media_social_player_image_thumbnail"
                name="MediaSocialPlayerImageThumbnail"
                afterChangeAction={(res) => {
                  setValue('MediaSocialPlayerImageThumbnail', res);
                  trigger('MediaSocialPlayerImageThumbnail');
                }}
              />

              <GeneralDatePicker
                minDate={new Date()}
                label="تاریخ شروع"
                name="MediaSocialPlayerDateTimeStart"
                value={startDate}
                onChangeValue={setStartDate}
              />

              <GeneralDatePicker
                minDate={startDate}
                label="تاریخ انقضا"
                name="MediaSocialPlayerDateTimeEnd"
                value={endDate}
                onChangeValue={setEndDate}
              />

              <RHFTextField name="MediaSocialPlayerClickUrl" type="text" label="لینک کلیک" />

              <RHFTextField name="MediaSocialPlayerVastUrl" type="text" label="لینک VAST (ویدیو تبلیغاتی)" />

              <RHFTextField name="MediaSocialPlayerOrderIndex" type="text" label="ترتیب نمایش " />
            </Box>

            <div className="flex items-center gap-4 mt-32">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default MediaSocialPlayerForm;
